import {isMobile} from '../modules/breakpoint.js';
import {deferToIdle} from '../utils/deferToIdle.js';

const MENU_STATE_CHANGE_EVENT_NAME = 'offCanvasMenu:stateChange';
const BODY_SCROLL_LOCK_DATA_KEY = 'offcanvasPreviousOverflow';
const ROOT_SCROLLBAR_GUTTER_DATA_KEY = 'offcanvasPreviousScrollbarGutter';

export class OffCanvasMenu {
    static selector = {
        toggle: '.js-openOffCanvas',
        panel: '.js-offCanvasMenu',
        panelContent: '.js-offCanvasMenuContent',
        backdrop: '.js-offCanvasMenuBackdrop',
    };

    // Blade renders the toggle's own open/close labels, so the copy stays in the server-side catalogs.
    static attribute = {
        expanded: 'aria-expanded',
        hidden: 'aria-hidden',
        openLabel: 'data-open-label',
        closeLabel: 'data-close-label',
    };

    static className = {
        hidden: 'hidden',
        closing: 'offcanvas-menu--closing',
        backdropVisible: 'is-visible',
    };

    // The default variant animates the inner content with `slide-to-top`. The
    // side-panel variant cancels that and slides the whole <nav> with
    // `slide-to-left`. We accept either so the closed state is reached regardless
    // of which element actually animated.
    static animation = {
        closeNames: ['slide-to-top', 'slide-to-left'],
    };

    #toggle;
    #panel;
    #panelContent;
    #backdrop;
    #controller;
    #closeAnimationController;
    #isOpen = false;

    constructor() {
        this.#bindElements();

        if (!this.#toggle || !this.#panel || !this.#panelContent) {
            return;
        }

        this.#isOpen = !this.#panel.classList.contains(OffCanvasMenu.className.hidden);
        this.#syncAccessibilityState();
        this.#applyToggleState(this.#isOpen);
        this.#applyBackdropState(this.#isOpen);
        this.#bindEvents();
    }

    #bindElements() {
        const {toggle, panel, backdrop} = OffCanvasMenu.selector;
        this.#toggle = document.querySelector(toggle);
        this.#panel = document.querySelector(panel);
        this.#panelContent = this.#panel?.querySelector(
            OffCanvasMenu.selector.panelContent
        );
        this.#backdrop = document.querySelector(backdrop);
    }

    #bindEvents() {
        this.#controller = new AbortController();
        const {signal} = this.#controller;

        this.#toggle.addEventListener('click', this.#handleToggleClick, {signal});
        document.addEventListener('click', this.#handleDocumentClick, {signal});
        document.addEventListener('keydown', this.#handleKeydown, {signal});
        document.addEventListener(
            'modalSearch:openCloseStateChange',
            this.#handleModalSearchStateChange,
            {signal}
        );

        if (this.#backdrop) {
            this.#backdrop.addEventListener('click', this.#handleBackdropClick, {signal});
        }
    }

    #handleBackdropClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.close();
    };

    #handleToggleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();

        // Pointer activations report event.detail > 0; keyboard activations report 0.
        // Strip focus on pointer activation so the focus ring doesn't linger over the icon.
        if (event.detail > 0) {
            this.#toggle.blur();
        }
    };

    #handleDocumentClick = (event) => {
        if (!(event.target instanceof Node)) {
            return;
        }

        if (
            !this.#isOpen ||
            this.#panel.contains(event.target) ||
            this.#toggle.contains(event.target)
        ) {
            return;
        }

        this.close();
    };

    #handleKeydown = (event) => {
        if (event.key === 'Escape' && this.#isOpen) {
            this.close();
        }
    };

    #handleModalSearchStateChange = (event) => {
        if (event.detail?.modalIsOpen) {
            this.close();
        }
    };

    toggle() {
        this.#isOpen ? this.close() : this.open();
    }

    open() {
        if (this.#isOpen) return;

        this.#isOpen = true;
        this.#cancelPendingClose();
        this.#panel.classList.remove(OffCanvasMenu.className.hidden);
        this.#panel.classList.remove(OffCanvasMenu.className.closing);
        this.#panel.setAttribute(OffCanvasMenu.attribute.hidden, 'false');
        this.#toggle.setAttribute(OffCanvasMenu.attribute.expanded, 'true');
        this.#applyToggleState(true);
        this.#applyBackdropState(true);
        if (this.#shouldLockBodyScroll()) {
            this.#lockBodyScroll();
        }
        this.#dispatchStateChange(true);
    }

    close() {
        if (!this.#isOpen) return;

        this.#isOpen = false;

        // Update accessibility and UI state immediately
        this.#panel.setAttribute(OffCanvasMenu.attribute.hidden, 'true');
        this.#toggle.setAttribute(OffCanvasMenu.attribute.expanded, 'false');
        this.#applyToggleState(false);
        this.#applyBackdropState(false);
        if (this.#shouldLockBodyScroll()) {
            this.#unlockBodyScroll();
        }
        this.#dispatchStateChange(false);

        this.#panel.classList.add(OffCanvasMenu.className.closing);
        this.#closeAnimationController = new AbortController();
        // animationend bubbles, so listen on the panel to catch the close animation
        // whether it ran on the inner content (default variant) or on the <nav>
        // itself (side-panel variant at md+).
        this.#panel.addEventListener('animationend', this.#handleCloseAnimationEnd, {
            signal: this.#closeAnimationController.signal,
        });
    }

    #handleCloseAnimationEnd = (event) => {
        if (this.#isOpen) return;
        if (event.target !== this.#panel && event.target !== this.#panelContent) return;
        if (!OffCanvasMenu.animation.closeNames.includes(event.animationName)) return;

        this.#panel.classList.add(OffCanvasMenu.className.hidden);
        this.#panel.classList.remove(OffCanvasMenu.className.closing);
        this.#closeAnimationController?.abort();
        this.#closeAnimationController = undefined;
    };

    #cancelPendingClose() {
        this.#closeAnimationController?.abort();
        this.#closeAnimationController = undefined;
    }

    #applyBackdropState(isOpen) {
        if (!this.#backdrop) return;

        this.#backdrop.classList.toggle(OffCanvasMenu.className.backdropVisible, isOpen);
    }

    #shouldLockBodyScroll() {
        return this.#panel?.dataset.lockBodyScroll !== 'false';
    }

    #syncAccessibilityState() {
        this.#panel.setAttribute(
            OffCanvasMenu.attribute.hidden,
            this.#isOpen ? 'false' : 'true'
        );
        this.#toggle.setAttribute(
            OffCanvasMenu.attribute.expanded,
            this.#isOpen ? 'true' : 'false'
        );
    }

    #applyToggleState(isOpen) {
        if (!this.#toggle) return;

        const labelAttribute = isOpen ? OffCanvasMenu.attribute.closeLabel : OffCanvasMenu.attribute.openLabel;
        const label = this.#toggle.getAttribute(labelAttribute);
        this.#toggle.dataset.menuState = isOpen ? 'open' : 'closed';
        this.#toggle.setAttribute('aria-label', label);
    }

    #lockBodyScroll() {
        if (document.body.dataset[BODY_SCROLL_LOCK_DATA_KEY] !== undefined) {
            return;
        }

        document.body.dataset[BODY_SCROLL_LOCK_DATA_KEY] = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        this.#releaseRootScrollbarGutter();
    }

    #unlockBodyScroll() {
        const previousOverflow = document.body.dataset[BODY_SCROLL_LOCK_DATA_KEY];

        if (previousOverflow === undefined) {
            return;
        }

        document.body.style.overflow = previousOverflow;
        delete document.body.dataset[BODY_SCROLL_LOCK_DATA_KEY];
        this.#restoreRootScrollbarGutter();
    }

    // Mobile drawers fill 100vw, so a `scrollbar-gutter: stable` reservation on
    // <html> would show a stripe on the right that the drawer can't cover.
    // Drop it for the duration of the lock; desktop keeps the gutter to avoid
    // the usual content-shift when the body scrollbar disappears.
    #releaseRootScrollbarGutter() {
        if (
            document.documentElement.dataset[ROOT_SCROLLBAR_GUTTER_DATA_KEY] !== undefined ||
            !isMobile()
        ) {
            return;
        }

        document.documentElement.dataset[ROOT_SCROLLBAR_GUTTER_DATA_KEY] =
            document.documentElement.style.scrollbarGutter;
        document.documentElement.style.scrollbarGutter = 'auto';
    }

    #restoreRootScrollbarGutter() {
        const previousScrollbarGutter =
            document.documentElement.dataset[ROOT_SCROLLBAR_GUTTER_DATA_KEY];

        if (previousScrollbarGutter === undefined) {
            return;
        }

        document.documentElement.style.scrollbarGutter = previousScrollbarGutter;
        delete document.documentElement.dataset[ROOT_SCROLLBAR_GUTTER_DATA_KEY];
    }

    #dispatchStateChange(open) {
        document.dispatchEvent(
            new CustomEvent(MENU_STATE_CHANGE_EVENT_NAME, {
                detail: {
                    menuIsOpen: open,
                },
            })
        );
    }

    static init() {
        new OffCanvasMenu();
    }
}

// Defer initialization to idle time — menu is hidden by default
document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', () => deferToIdle(OffCanvasMenu.init))
    : deferToIdle(OffCanvasMenu.init);
